// Catalogue wire contracts — books + copies + the authority registries
// (author/publisher/subject/tag) that db/migrations/0002_catalogue.sql backs.
// Browser-safe zod only.
//
// Linking law (pinned — NO join tables): one author per book via
// books.author_id + the denormalised books.author name cache; publisher and
// subject link by NAME equality (find-or-create registry rows); tag is a
// standalone registry. Extra authors ride raw_marc_json.additionalAuthors.
import { z } from "zod";
import { CategoryHue, CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";

/* ─────────────── copy vocabulary (circulation drives the transitions) ─────────────── */

export const COPY_STATUSES = ["Available", "Checked Out", "Reserved", "Damaged", "Lost", "In Transit"] as const;
export const CopyStatus = z.enum(COPY_STATUSES);
export type CopyStatus = z.infer<typeof CopyStatus>;

/**
 * Copies that block withdrawal/deletion — loan-activity is encoded purely in
 * copy status (no loans table in this slice; the source already worked this
 * way). 'Reserved' is manually-set-only: no server code path ever sets it —
 * it enters exclusively via POST/PATCH copy (reserved-status-note divergence).
 */
export const ACTIVE_COPY_STATUSES = ["Checked Out", "Reserved", "In Transit"] as const satisfies readonly CopyStatus[];

/** Write-side condition enum (create/patch surface). */
export const BOOK_CONDITIONS = ["New", "Good", "Fair", "Poor"] as const;
export const BookCondition = z.enum(BOOK_CONDITIONS);
export type BookCondition = z.infer<typeof BookCondition>;

/** What the column actually holds — circulation returns/mark-damaged also write 'Damaged'. */
export const StoredBookCondition = z.enum([...BOOK_CONDITIONS, "Damaged"]);
export type StoredBookCondition = z.infer<typeof StoredBookCondition>;

/**
 * Derived title-level status — NEVER stored: avail>0 → 'Available'; else
 * reserved>0 → 'Reserved'; else total>0 → 'Checked Out'; zero copies →
 * 'Available' (source catalogue.mappers.ts:13-14).
 */
export const BOOK_STATUSES = ["Available", "Checked Out", "Reserved"] as const;
export const BookStatus = z.enum(BOOK_STATUSES);
export type BookStatus = z.infer<typeof BookStatus>;

/* ───────────────────────────── accession number (0013) ───────────────────────────── */

/**
 * The stamped ACCESSION NUMBER — the short internal number (often four digits)
 * written inside the physical volume. It is PER COPY, never per title: two
 * copies of the same book carry different numbers, which is why it lives on
 * lib_copy. Nullable everywhere (a library that keeps no accession register
 * never sets one) and UNIQUE library-wide once set (0013's nullable+UNIQUE
 * index). 32 chars is generous for a register number and keeps it a
 * scannable/typeable key rather than a free-text note.
 */
export const ACCESSION_NO_MAX = 32;
const accessionNo = z.string().trim().min(1).max(ACCESSION_NO_MAX);

/**
 * Positional accession numbers for a batch accession — entry i stamps the i-th
 * minted copy; a shorter list leaves the rest unstamped. Uniqueness INSIDE the
 * list is a schema-level refusal (the batch would collide with itself); a
 * collision with the existing register is the LIB_COPY_ACCESSION_TAKEN 409.
 */
export const AccessionNoList = z
  .array(accessionNo)
  .max(500)
  .refine((xs) => new Set(xs).size === xs.length, { message: "Accession numbers must be unique within the batch" });

/* ─────────────────────────────────── books ─────────────────────────────────── */

/**
 * The raw_marc_json ALLOWLIST — every extended descriptive form field with no
 * first-class column (the BOOK_COLS complement, source catalogue.service.ts:37-40).
 * Stored under the create form's own camelCase keys and read back verbatim
 * (toBookDetail) — any re-keying breaks the edit round-trip, and ETL ports the
 * blob byte-for-byte. Spread FLAT on the write shapes below.
 */
/* ── Cataloguing vocabularies ──────────────────────────────────────────────
 * RDA/MARC value lists offered as SELECTS, so two cataloguers spell a carrier
 * the same way. They live here rather than beside one form because two screens
 * ask the question now — the single Add Book form and bulk add's row editor —
 * and a vocabulary duplicated is a vocabulary that drifts.
 *
 * Not enums on the column: `contentType` and friends ride raw_marc_json as free
 * text, and a record imported from MARC may legitimately carry a term this list
 * has never heard of. The list guides typing; it does not police the data.
 */
/** RDA / MARC 336. */
export const CONTENT_TYPES = ["text", "still image", "cartographic image", "notated music", "spoken word", "tactile text"] as const;
/** RDA / MARC 338. */
export const CARRIER_TYPES = ["volume", "audio disc", "computer disc", "online resource", "microfilm reel", "sheet"] as const;
/** Physical binding — drives shelf handling and, on some policies, the loan period. */
export const BOOK_FORMATS = ["Hardcover", "Paperback", "Board book", "Spiral-bound", "Library binding", "Loose-leaf"] as const;

export const BookExtra = z
  .object({
    additionalAuthors: z.string(),
    statementOfResponsibility: z.string(),
    place: z.string(),
    series: z.string(),
    seriesNumber: z.string(),
    originalYear: z.number().int(),
    pages: z.string(),
    dimensions: z.string(),
    weight: z.string(),
    format: z.string(),
    contentType: z.string(),
    carrierType: z.string(),
    audience: z.string(),
    condition: z.string(),
    lcCallNumber: z.string(),
    lccn: z.string(),
    oclc: z.string(),
    otherId: z.string(),
    genre: z.string(),
    generalNote: z.string(),
    tocNote: z.string(),
    keywords: z.array(z.string()),
  })
  .partial();
export type BookExtra = z.infer<typeof BookExtra>;

// ── MATERIAL TYPES (2026-09-08) ────────────────────────────────────────────
//
// THE CARRIER — what the thing physically is. Not how it is issued, and not how
// it circulates; those are two other questions with two other homes, and the
// commonest modelling mistake here is to answer all three with one field.
//
//   WHAT IT IS        this enum. MARC Leader/06 "type of record"; FOLIO
//                     instanceType; Koha itemtype.
//   HOW IT IS ISSUED  MARC Leader/07 "bibliographic level"; FOLIO
//                     modeOfIssuance. Answered PHYSICALLY here: monograph-level
//                     things are rows in lib_book, serial-level things are
//                     subscriptions in lib_serial_subscription. WHICH IS WHY
//                     "magazine", "journal" and "newspaper" ARE ABSENT — a
//                     magazine is text issued serially and already has a home,
//                     and adding it here would give periodicals two models and
//                     two answers to "what do we subscribe to".
//   HOW IT CIRCULATES CopyCirculation below, on the COPY.
//
// NINE VALUES, and the count is deliberate. Every value is a facet in the
// catalogue, a branch in the form, a row in the settings table and a word to
// translate — so "public records" and "civic paperwork" are one
// `government_document` rather than two, and the distinction lives in `subject`
// where a librarian can actually use it.
export const MATERIAL_TYPES = [
  "book",
  "atlas",
  "map",
  "score",
  "manuscript",
  "government_document",
  "ephemera",
  "zine",
  "newspaper_volume",
] as const;
export const MaterialType = z.enum(MATERIAL_TYPES);
export type MaterialType = z.infer<typeof MaterialType>;

/** What a person sees. Never render the raw enum — these are the only labels. */
export const MATERIAL_TYPE_LABELS: Record<MaterialType, string> = {
  book: "Book",
  atlas: "Atlas",
  map: "Map",
  score: "Sheet music",
  manuscript: "Manuscript",
  government_document: "Government document",
  ephemera: "Ephemera",
  zine: "Zine",
  newspaper_volume: "Bound newspapers",
};

// ── Does THIS copy leave the building? ─────────────────────────────────────
//
// ON THE COPY, never on the title. A reference collection IS one copy that
// stays beside others that circulate, and a title-level flag cannot express it —
// which is why Koha puts it on the item (notforloan), FOLIO on the item
// (permanentLoanType) and Alma on the item (item policy). Convergent, not a
// house invention.
export const COPY_CIRCULATIONS = ["circulating", "in_library_only"] as const;
export const CopyCirculation = z.enum(COPY_CIRCULATIONS);
export type CopyCirculation = z.infer<typeof CopyCirculation>;

export const COPY_CIRCULATION_LABELS: Record<CopyCirculation, string> = {
  circulating: "Can be borrowed",
  in_library_only: "Reading room only",
};

/**
 * The DEFAULT circulation a new copy gets from its title's material type.
 *
 * A DEFAULT, NOT THE AUTHORITY — the copy's own value always wins, and the
 * library can override this whole map in settings. Shipped values follow
 * ordinary practice: archives and bound newspaper runs are consulted in the
 * reading room, and maps are large-format and rarely lent.
 */
export const DEFAULT_CIRCULATION_BY_TYPE: Record<MaterialType, CopyCirculation> = {
  book: "circulating",
  atlas: "circulating",
  map: "in_library_only",
  score: "circulating",
  manuscript: "in_library_only",
  government_document: "circulating",
  ephemera: "circulating",
  zine: "circulating",
  newspaper_volume: "in_library_only",
};

/**
 * Per-type descriptive fields, stored in `metadata_json`.
 *
 * JSON AND NOT COLUMNS because as columns this is ~20 sparse fields on a table
 * already carrying 22, and D1 caps a table at 100 COLUMNS — a hard limit, not a
 * guideline (INFRA-02). Every field is optional: a catalogue record that must be
 * complete is a catalogue record nobody finishes.
 */
export const MaterialMetadata = z
  .object({
    /** map, atlas — "1:50,000". Free text: scales are written a dozen ways. */
    scale: z.string().max(60),
    /** map, atlas — what ground it covers, e.g. "Hamirpur district". */
    coverage: z.string().max(200),
    /** map — "UTM Zone 43N". */
    projection: z.string().max(60),
    /** score — "SATB with piano", "solo guitar". */
    instrumentation: z.string().max(200),
    /** score — "D minor". */
    musicalKey: z.string().max(40),
    /** manuscript — "3 boxes, 1.2 linear metres". */
    extent: z.string().max(120),
    /** manuscript — where it came from. The archival question. */
    provenance: z.string().max(500),
    /** government_document — the issuing body, which is often not the publisher. */
    issuingBody: z.string().max(200),
    /** government_document — a departmental or SuDoc-style number. */
    documentNumber: z.string().max(80),
    /** newspaper_volume — the span a bound run covers, e.g. "Jan–Jun 1974". */
    coveragePeriod: z.string().max(120),
  })
  .partial();
export type MaterialMetadata = z.infer<typeof MaterialMetadata>;

/**
 * The per-type circulation map, as a schema.
 *
 * Declared ONCE and reused by the settings DTO, the settings write body and the
 * service's read-merge — three places that must agree about what a valid map is,
 * and would drift if each built its own.
 */
export const CirculationByMaterialType = z.object(
  Object.fromEntries(MATERIAL_TYPES.map((t) => [t, CopyCirculation])) as {
    [K in MaterialType]: typeof CopyCirculation;
  },
);
export type CirculationByMaterialType = z.infer<typeof CirculationByMaterialType>;

/** What each metadata field is called on screen. Never render the raw key. */
export const METADATA_FIELD_LABELS: Record<keyof MaterialMetadata, string> = {
  scale: "Scale",
  coverage: "Area covered",
  projection: "Projection",
  instrumentation: "Instrumentation",
  musicalKey: "Key",
  extent: "Extent",
  provenance: "Provenance",
  issuingBody: "Issuing body",
  documentNumber: "Document number",
  coveragePeriod: "Period covered",
};

/** Which metadata fields a given type actually shows. The form reads this. */
export const METADATA_FIELDS_BY_TYPE: Record<MaterialType, readonly (keyof MaterialMetadata)[]> = {
  book: [],
  atlas: ["scale", "coverage"],
  map: ["scale", "coverage", "projection"],
  score: ["instrumentation", "musicalKey"],
  manuscript: ["extent", "provenance"],
  government_document: ["issuingBody", "documentNumber"],
  ephemera: [],
  zine: [],
  newspaper_volume: ["coveragePeriod"],
};

/** The thin list DTO — copy counts via GROUP BY over lib_copy, status derived. */
export const Book = z.object({
  id: EntityId, // 'bk-…' ports verbatim; new rows mint ULIDs
  title: z.string(),
  subtitle: z.string().nullable(),
  // NULLABLE since the material-types slice: only a title is mandatory in MARC,
  // and a flyer, an anonymous zine or a census report genuinely has no author.
  author: z.string().nullable(),
  /**
   * The authority row behind that name, when there is one. It lives on the
   * LIST shape (not only on BookDetail, where it sat until 2026-10-02) because
   * a register's row makes the author a place to go: without the id the only
   * honest rendering is flat text. The column was already selected by the list
   * query, so carrying it costs nothing. ON DELETE SET NULL — an unguarded
   * author delete nulls the link and the name cache above survives.
   */
  authorId: EntityId.nullable(),
  isbn: z.string().nullable(),
  publisher: z.string().nullable(), // NAME link — no FK (find-or-create registry row)
  publicationYear: z.number().int().nullable(),
  edition: z.string().nullable(),
  language: z.string(), // NOT NULL DEFAULT 'English' in DDL (nullability decision; ETL coalesces)
  subject: z.string().nullable(), // NAME link
  library: z.string().nullable(), // NAME link
  classificationCode: z.string().nullable(),
  description: z.string().nullable(),
  isDigital: z.boolean(), // ← is_digital_only INT 0/1
  totalCopies: z.number().int().min(0), // derived: COUNT(lib_copy)
  availableCopies: z.number().int().min(0), // derived
  status: BookStatus, // derived — never stored
  /**
   * WHERE TO WALK (2026-09-22, the public catalogue's compact card): the rack
   * label — `lib_shelf.code`, what is printed on the shelf end — of an AVAILABLE
   * copy of this title that has been placed on a shelf; null when none is (no
   * copies, every copy out, or the available ones not yet shelved). Derived at
   * read time from lib_copy ⨝ lib_shelf like the counts beside it, never stored.
   * When several available copies sit on different shelves this is the LOWEST
   * code (MIN), so the same title always names the same rack — one deterministic
   * place to walk to, not "the first row the planner happened to return".
   */
  shelfCode: z.string().nullable(),
  /** Always null (kept for wire compat) — thumbnails resolve coverKey via POST /catalogue/asset-urls, never a stored URL. */
  coverUrl: z.string().nullable(),
  /** The bare R2 upload key ('covers/cvr_…') — the FE's handle into the batch asset-URL serve. */
  coverKey: z.string().nullable(),
  /** The carrier. 'book' for everything catalogued before this existed. */
  materialType: MaterialType,
  /** Per-type descriptive fields; `{}` when the type declares none. */
  metadata: MaterialMetadata,
});
export type Book = z.infer<typeof Book>;

/**
 * Rich single-book view (GET /books/:id only — the list stays thin): Book +
 * the authority link + every extended field read back from raw_marc_json,
 * always present, null when unstored (source toBookDetail parity).
 */
export const BookDetail = Book.extend({
  additionalAuthors: z.string().nullable(),
  statementOfResponsibility: z.string().nullable(),
  place: z.string().nullable(),
  originalYear: z.number().int().nullable(),
  series: z.string().nullable(),
  seriesNumber: z.string().nullable(),
  pages: z.string().nullable(),
  dimensions: z.string().nullable(),
  weight: z.string().nullable(),
  format: z.string().nullable(),
  contentType: z.string().nullable(),
  carrierType: z.string().nullable(),
  audience: z.string().nullable(),
  condition: z.string().nullable(),
  lcCallNumber: z.string().nullable(),
  lccn: z.string().nullable(),
  oclc: z.string().nullable(),
  otherId: z.string().nullable(),
  genre: z.string().nullable(),
  generalNote: z.string().nullable(),
  tocNote: z.string().nullable(),
  keywords: z.array(z.string()).nullable(),
});
export type BookDetail = z.infer<typeof BookDetail>;

// No-default validator core shared by create/update (truly-partial PATCH law —
// an omitted field must never reset to a default).
const bookCore = {
  title: z.string().trim().min(1),
  subtitle: z.string(),
  author: z.string().trim().min(1),
  /**
   * Authority picker — resolveRefs: when present, the chosen author's
   * CANONICAL name overwrites the sent name; omitted = find-or-create the
   * registry row by exact name.
   */
  authorId: EntityId,
  /**
   * OPTIONAL NOW, and still validated when present.
   *
   * Requiring it made the catalogue book-only: a map, a manuscript, a score and
   * a government report have no ISBN, so none of them could be created at all.
   * Dropping the length check entirely would have been the easier change and the
   * wrong one — it is the only quality signal on the field, and books, which are
   * most of the catalogue, still carry one.
   */
  isbn: z.string().trim().min(8).max(20).nullable(),
  publisher: z.string(),
  publicationYear: z.number().int().min(0).max(2100),
  edition: z.string(),
  language: z.string(),
  subject: z.string(),
  library: z.string(),
  /** The create form's key — stored as classification_code, read back as classificationCode. */
  classification: z.string(),
  description: z.string(),
  isDigital: z.boolean(),
  coverKey: z.string(),
};

export const CreateBook = z.object({
  title: bookCore.title,
  /**
   * OPTIONAL. Only the title is mandatory — MARC's own rule, and the one that
   * lets an anonymous zine, a flyer or a census report be catalogued at all.
   */
  author: bookCore.author.optional(),
  /**
   * OPTIONAL AND NULLABLE — absent and explicitly-none are both legitimate.
   * `.nullable()` alone still demanded the key be present, which meant a map
   * had to send `isbn: null` to be created at all.
   */
  isbn: bookCore.isbn.optional(),
  /** The carrier. Defaults to 'book', which is what the catalogue held before. */
  materialType: MaterialType.default("book"),
  /** Per-type descriptive fields; ignored for types that declare none. */
  metadata: MaterialMetadata.default({}),
  subtitle: bookCore.subtitle.optional(),
  authorId: bookCore.authorId.optional(),
  publisher: bookCore.publisher.optional(),
  publicationYear: bookCore.publicationYear.optional(),
  edition: bookCore.edition.optional(),
  language: bookCore.language.default("English"), // mirrors the DDL default
  subject: bookCore.subject.optional(),
  library: bookCore.library.optional(),
  classification: bookCore.classification.optional(),
  description: bookCore.description.optional(),
  isDigital: bookCore.isDigital.default(false),
  coverKey: bookCore.coverKey.optional(),
  // extended descriptive fields ride FLAT → raw_marc_json (never lost)
  ...BookExtra.shape,
  // accession — atomically create N copies with the title
  copies: z.number().int().min(0).max(500).optional(),
  branchId: EntityId.optional(),
  shelfId: EntityId.optional(),
  /** Positional accession numbers for the copies minted with the title (AddCopiesBatch semantics). */
  accessionNos: AccessionNoList.optional(),
});
export type CreateBook = z.infer<typeof CreateBook>;

/**
 * Create minus accession (copies/branchId/shelfId — zod's unknown-key
 * stripping makes sending them a silent no-op), all partial; extras MERGE
 * into the stored raw_marc_json server-side, never wipe it.
 */
export const UpdateBook = z.object(bookCore).partial().extend(BookExtra.shape);
export type UpdateBook = z.infer<typeof UpdateBook>;

/** The source's sort whitelist verbatim — now the keyset key set (keyset-pagination divergence). */
export const BOOK_SORTS = [
  "title:asc",
  "title:desc",
  "isbn:asc",
  "isbn:desc",
  "publicationYear:asc",
  "publicationYear:desc",
  "author:asc",
  "author:desc",
] as const;
export const BookSort = z.enum(BOOK_SORTS);
export type BookSort = z.infer<typeof BookSort>;

/**
 * Keyset list. `search` rides FTS5 MATCH over lib_book_fts (title/subtitle/
 * author/isbn/publisher/subject/description) — replaces the source's
 * unindexable LIKE '%q%'. Exact-eq filters hit plain indexed columns; `status`
 * keeps the source's HAVING semantics: Available = avail>0; 'Checked Out' =
 * avail=0 AND total>0; Reserved = reserved>0.
 */
export const BookListQuery = CursorQuery.extend({
  search: z.string().trim().min(1).max(200).optional(),
  sort: BookSort.default("title:asc"),
  subject: z.string().optional(),
  library: z.string().optional(),
  author: z.string().optional(),
  publisher: z.string().optional(),
  status: BookStatus.optional(),
  /** The material-type facet. Backed by the composite (material_type, title, id) index. */
  materialType: MaterialType.optional(),
  /**
   * Publication-year window, INCLUSIVE both ends, either end optional.
   *
   * Server-side on purpose. The console filtered the year in the browser over
   * the twenty rows of the current keyset page, which looks catalogue-wide and
   * is not: a title published in 1962 sitting on page 7 was simply invisible to
   * a 1960–1970 filter. A facet that only searches what you can already see is
   * worse than no facet, because it answers confidently.
   */
  yearFrom: z.coerce.number().int().min(0).max(9999).optional(),
  yearTo: z.coerce.number().int().min(0).max(9999).optional(),
  /**
   * Print vs eBook — `is_digital_only`, the sole books-side digital flag.
   *
   * NOT called `edition`: lib_book already has an `edition` COLUMN carrying the
   * edition statement ("2nd ed."), and two different meanings under one name in
   * the same query object is a trap for whoever writes the next clause.
   */
  format: z.enum(["print", "ebook"]).optional(),
});
export type BookListQuery = z.infer<typeof BookListQuery>;

/** ?withdrawCopies= — stringbool, never z.coerce.boolean ("false" must parse false). */
export const DeleteBookQuery = z.object({ withdrawCopies: z.stringbool().default(false) });
export type DeleteBookQuery = z.infer<typeof DeleteBookQuery>;

/** DELETE /books/:id → withdrawn = how many copies went with the title. */
export const DeleteBookResult = z.object({ id: EntityId, withdrawn: z.number().int().min(0) });
export type DeleteBookResult = z.infer<typeof DeleteBookResult>;

export const BulkDeleteBooksBody = z.object({
  /** Capped at 100 — bulk pre-checks and deletes ride chunked statements, not per-id loops (R3). */
  ids: z.array(EntityId).min(1).max(100),
  withdrawCopies: z.boolean().optional(),
});
export type BulkDeleteBooksBody = z.infer<typeof BulkDeleteBooksBody>;

/** Skipped = kept: has copies (withdrawCopies off) or an active copy (on) — never an error. */
export const BulkDeleteBooksResult = z.object({ deleted: z.array(EntityId), skipped: z.array(EntityId) });
export type BulkDeleteBooksResult = z.infer<typeof BulkDeleteBooksResult>;

/**
 * GET /catalogue/search — q is user text compiled to an FTS5 MATCH; capped at
 * 200 chars like every list `search` (R2 — an unbounded q was the one
 * uncapped user-text path). Tokenless/empty q degrades to an empty page.
 */
export const CatalogueSearchQuery = CursorQuery.extend({
  q: z.string().max(200).default(""),
});
export type CatalogueSearchQuery = z.infer<typeof CatalogueSearchQuery>;

/** Header cards. checkedOut = total − available (NOT a status='Checked Out' count — source parity). */
export const CatalogueStats = z.object({
  titles: z.number().int().min(0),
  totalCopies: z.number().int().min(0),
  availableCopies: z.number().int().min(0),
  checkedOut: z.number().int().min(0),
});
export type CatalogueStats = z.infer<typeof CatalogueStats>;

/* ─────────────────────────────────── copies ────────────────────────────────── */

/** A copy's place on the floor, by name — see Copy.location. */
export const CopyLocation = z.object({
  library: z.string().nullable(),
  branch: z.string().nullable(),
  zone: z.string().nullable(),
  stack: z.string().nullable(),
  shelf: z.string().nullable(),
  /** The shelf's code — what is printed on the rack. */
  shelfCode: z.string().nullable(),
});
export type CopyLocation = z.infer<typeof CopyLocation>;

export const Copy = z.object({
  /** Surrogate ULID PK (copies-surrogate-pk divergence) — barcode stays the business key, UNIQUE. */
  id: EntityId,
  barcode: z.string(),
  /** 0013 — NULL until the volume is stamped; UNIQUE across the library once it is. */
  accessionNo: z.string().nullable(),
  bookId: EntityId,
  branchId: EntityId.nullable(),
  shelfId: EntityId.nullable(),
  /**
   * Where the volume sits, by NAME, top down: library › branch › zone › stack
   * › shelf (the shelf's code beside it — the label on the rack). Resolved by
   * the read (sql.ts COPY_SELECT), never stored twice; a level that is unset
   * is null, and a copy with no shelf and no branch has every field null.
   */
  location: CopyLocation,
  status: CopyStatus,
  acquiredDate: EpochMs,
  bookCondition: StoredBookCondition, // NOT NULL DEFAULT 'Good' (ETL coalesces source NULLs)
  /**
   * Whether THIS physical copy may leave the building.
   *
   * Defaulted at creation from the title's material type
   * (DEFAULT_CIRCULATION_BY_TYPE) and overridable per copy — which is the whole
   * point: a reference collection is one copy that stays beside others that go.
   */
  circulation: CopyCirculation,
});
export type Copy = z.infer<typeof Copy>;

const copyCore = {
  /**
   * Stated only when the librarian is overriding the material type's default —
   * a reference copy of an ordinary book, or a lendable copy of something the
   * library normally keeps in.
   */
  circulation: CopyCirculation,
  barcode: z.string().trim().min(4),
  accessionNo,
  branchId: EntityId,
  shelfId: EntityId,
  status: CopyStatus,
  bookCondition: BookCondition,
};

export const CreateCopy = z.object({
  /**
   * OPTIONAL — omit it and the copy's own id becomes its barcode.
   *
   * Left available for stock that arrives ALREADY LABELLED by a vendor, which
   * is the only case where a barcode this system did not mint is the truth on
   * the spine. Nothing in the product asks an operator to invent one.
   */
  barcode: copyCore.barcode.optional(),
  /** Omit when the volume is not stamped; a number already in use is LIB_COPY_ACCESSION_TAKEN. */
  accessionNo: copyCore.accessionNo.optional(),
  bookId: EntityId,
  branchId: copyCore.branchId.optional(),
  shelfId: copyCore.shelfId.optional(),
  /** Copies are born 'Available' — and explicit create/patch is the ONLY path that can set 'Reserved'. */
  status: copyCore.status.default("Available"),
  /** Omit to inherit the title's material-type default (DEFAULT_CIRCULATION_BY_TYPE). */
  circulation: copyCore.circulation.optional(),
  acquiredDate: EpochMs.optional(), // server fills now() when omitted
  bookCondition: copyCore.bookCondition.default("Good"),
});
export type CreateCopy = z.infer<typeof CreateCopy>;

/** PATCH /copies/:barcode — truly partial; an omitted field stays untouched. */
export const UpdateCopy = z
  .object({
    /** NULLABLE on the patch (unlike the other keys): explicit null UNSTAMPS the copy. */
    accessionNo: copyCore.accessionNo.nullable(),
    status: copyCore.status,
    branchId: copyCore.branchId,
    shelfId: copyCore.shelfId,
    bookCondition: copyCore.bookCondition,
    /**
     * The per-copy override, CHANGEABLE.
     *
     * It was accepted at create and nowhere else, which made the copy-level
     * decision a one-shot: a volume miscatalogued as lending could never be
     * pulled into the reading room without deleting and re-accessioning it.
     * Copies are re-shelved and re-classified all the time; this has to move.
     */
    circulation: copyCore.circulation,
  })
  .partial();
export type UpdateCopy = z.infer<typeof UpdateCopy>;

/**
 * POST /books/:id/copies — batch accession with auto-generated barcodes.
 *
 * `accessionNos` is a LIST, not a single value: a batch mints `count` distinct
 * physical volumes and an accession number is unique per volume, so one shared
 * number could only ever be a duplicate. The list is positional (entry i stamps
 * the i-th minted copy) and may be shorter than `count` — the remaining copies
 * are simply unstamped. Duplicates inside the list are rejected by the schema;
 * collisions with numbers already in the register are LIB_COPY_ACCESSION_TAKEN.
 */
export const AddCopiesBatch = z.object({
  count: z.number().int().min(1).max(500).default(1),
  branchId: EntityId.optional(),
  shelfId: EntityId.optional(),
  bookCondition: BookCondition.optional(),
  accessionNos: AccessionNoList.optional(),
});
export type AddCopiesBatch = z.infer<typeof AddCopiesBatch>;

/* ─────────────────────────────────── authors ───────────────────────────────── */

/**
 * The extra_json ALLOWLIST — extended authority fields with no first-class
 * column, stored under the form's own keys (round-trip law as BookExtra).
 */
export const AuthorExtra = z
  .object({
    fullerName: z.string(),
    variantNames: z.string(),
    born: z.string(),
    died: z.string(),
    affiliation: z.string(),
    fieldOfActivity: z.string(),
    occupation: z.string(),
    associatedPlace: z.string(),
    website: z.string(),
    sourceOfHeading: z.string(),
    viaf: z.string(),
    lccn: z.string(),
    wikidata: z.string(),
    /**
     * Open Library author id (`OL529531A`). The CHEAPEST identity this
     * catalogue can obtain — the book lookup already talks to Open Library,
     * so an import that knows the title knows this without a second provider.
     * It sits LAST in AUTHORITY_PRIORITY for the same reason it is cheap: it
     * is per-source and Open Library merges its own author records.
     */
    olid: z.string(),
  })
  .partial();
export type AuthorExtra = z.infer<typeof AuthorExtra>;

// extra_json fields read back by the mapper — always present, null when unstored
const authorExtraRead = {
  fullerName: z.string().nullable(),
  variantNames: z.string().nullable(),
  born: z.string().nullable(),
  died: z.string().nullable(),
  affiliation: z.string().nullable(),
  fieldOfActivity: z.string().nullable(),
  occupation: z.string().nullable(),
  associatedPlace: z.string().nullable(),
  website: z.string().nullable(),
  sourceOfHeading: z.string().nullable(),
  viaf: z.string().nullable(),
  lccn: z.string().nullable(),
  wikidata: z.string().nullable(),
  olid: z.string().nullable(),
};

export const Author = z.object({
  id: EntityId, // 'at-…' ports verbatim
  name: z.string(),
  nationality: z.string().nullable(),
  languages: z.string().nullable(),
  subject: z.string().nullable(),
  bio: z.string().nullable(),
  imageKey: z.string().nullable(),
  orcid: z.string().nullable(),
  isni: z.string().nullable(),
  scopus: z.string().nullable(),
  researcherId: z.string().nullable(),
  /** Live COUNT(lib_book WHERE author_id = id) — the stored counter column is DROPPED (counter-strategy divergence). */
  booksCount: z.number().int().min(0),
  createdAt: EpochMs,
  ...authorExtraRead,
});
export type Author = z.infer<typeof Author>;

const authorCore = {
  name: z.string().trim().min(1),
  nationality: z.string(),
  languages: z.string(),
  subject: z.string(),
  bio: z.string(),
  imageKey: z.string(),
  orcid: z.string(),
  isni: z.string(),
  scopus: z.string(),
  researcherId: z.string(),
};

export const CreateAuthor = z.object({
  name: authorCore.name,
  nationality: authorCore.nationality.optional(),
  languages: authorCore.languages.optional(),
  subject: authorCore.subject.optional(),
  bio: authorCore.bio.optional(),
  imageKey: authorCore.imageKey.optional(),
  orcid: authorCore.orcid.optional(),
  isni: authorCore.isni.optional(),
  scopus: authorCore.scopus.optional(),
  researcherId: authorCore.researcherId.optional(),
  ...AuthorExtra.shape, // flat → extra_json
});
export type CreateAuthor = z.infer<typeof CreateAuthor>;

/** Extras MERGE over stored extra_json server-side, never wipe. */
export const UpdateAuthor = z.object(authorCore).partial().extend(AuthorExtra.shape);
export type UpdateAuthor = z.infer<typeof UpdateAuthor>;

/**
 * POST /authors/:id/merge — re-points each source's books (author_id = src OR
 * the legacy name-linked branch: author_id IS NULL AND author = src.name) to
 * the target id + canonical name, then deletes the source; atomic PER SOURCE.
 */
/** sourceIds capped at 100 — merge is atomic per source; unbounded arrays are a CPU-budget hole (R3). */
export const MergeAuthorsBody = z.object({ sourceIds: z.array(EntityId).min(1).max(100) });
export type MergeAuthorsBody = z.infer<typeof MergeAuthorsBody>;

export const MergeAuthorsResult = z.object({ author: Author, moved: z.number().int().min(0) });
export type MergeAuthorsResult = z.infer<typeof MergeAuthorsResult>;

/** Keyset list on (name, id); search: name|subject (prefix-LIKE on the name index — no FTS for the registries). */
export const AuthorListQuery = CursorQuery.extend({ search: z.string().trim().min(1).max(200).optional() });
export type AuthorListQuery = z.infer<typeof AuthorListQuery>;

/* ─────────────────── possible duplicates (GET /authors/duplicates) ──────────
 * SURFACED, NEVER MERGED. Everything here is evidence for a cataloguer; the
 * merge itself stays the existing POST /authors/:id/merge, with the existing
 * confirmation, because it re-points books and deletes rows.
 */

/**
 * Why these rows were put in one cluster.
 * - `identifier` — they share a registry identifier (VIAF, ORCID, ISNI, …).
 *   Strong evidence: a registry has already said these are one person.
 * - `name` — they share only a normalised name. WEAK evidence on purpose: two
 *   different people share a name often, which is why this can never merge
 *   anything by itself.
 */
export const AUTHOR_DUPLICATE_REASONS = ["identifier", "name"] as const;
export const AuthorDuplicateReason = z.enum(AUTHOR_DUPLICATE_REASONS);
export type AuthorDuplicateReason = z.infer<typeof AuthorDuplicateReason>;

/** One candidate row — exactly what the merge chooser needs to rank and label it. */
export const AuthorDuplicateMember = z.object({
  id: EntityId,
  name: z.string(),
  /** Live count, as the registry list reports it — the merge's default "keep" is the biggest. */
  booksCount: z.number().int().min(0),
  identityKey: z.string().nullable(),
  orcid: z.string().nullable(),
  isni: z.string().nullable(),
  /** Every canonical identifier this row carries, as "<scheme>:<value>" — the evidence, shown. */
  identifiers: z.array(z.string()),
});
export type AuthorDuplicateMember = z.infer<typeof AuthorDuplicateMember>;

export const AuthorDuplicateGroup = z.object({
  /** The strongest key that connects the cluster, e.g. "viaf:52010985" or "name:r k narayan". */
  key: z.string(),
  reason: AuthorDuplicateReason,
  members: z.array(AuthorDuplicateMember).min(2),
});
export type AuthorDuplicateGroup = z.infer<typeof AuthorDuplicateGroup>;

export const AuthorDuplicatesResult = z.object({
  groups: z.array(AuthorDuplicateGroup),
  /** How many rows were examined — so a partial answer says so instead of reading as "none left". */
  scanned: z.number().int().min(0),
  /** The scan hit its cap: there may be more clusters than these. */
  truncated: z.boolean(),
});
export type AuthorDuplicatesResult = z.infer<typeof AuthorDuplicatesResult>;

/** `limit` caps the CLUSTERS returned, not the rows scanned (that cap is the server's). */
export const AuthorDuplicatesQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type AuthorDuplicatesQuery = z.infer<typeof AuthorDuplicatesQuery>;

/* ────────────────────────────────── publishers ─────────────────────────────── */

/** The extra_json ALLOWLIST (round-trip law as BookExtra/AuthorExtra). */
export const PublisherExtra = z
  .object({
    parentCompany: z.string(),
    address: z.string(),
    postalCode: z.string(),
    gln: z.string(),
    ringgold: z.string(),
    crossref: z.string(),
    wikidata: z.string(),
    website: z.string(),
    email: z.string(),
    phone: z.string(),
    contactPerson: z.string(),
    distributor: z.string(),
    gstNumber: z.string(),
    notes: z.string(),
    /**
     * Every OTHER spelling this imprint has been seen under — "Penguin Books,
     * Limited" and "Penguin Books Ltd." recorded on the row whose canonical
     * name is "Penguin Books" (0025 / resolvePublisher).
     *
     * A record, not a decision: the registry keeps ONE row per imprint, and
     * this is where the strings it folded are kept so nothing a provider said
     * is lost and a cataloguer can see WHY the row matched.
     */
    variants: z.array(z.string()),
  })
  .partial();
export type PublisherExtra = z.infer<typeof PublisherExtra>;

const publisherExtraRead = {
  parentCompany: z.string().nullable(),
  address: z.string().nullable(),
  postalCode: z.string().nullable(),
  gln: z.string().nullable(),
  ringgold: z.string().nullable(),
  crossref: z.string().nullable(),
  wikidata: z.string().nullable(),
  website: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  contactPerson: z.string().nullable(),
  distributor: z.string().nullable(),
  gstNumber: z.string().nullable(),
  notes: z.string().nullable(),
  variants: z.array(z.string()).nullable(),
};

export const Publisher = z.object({
  id: EntityId, // 'pub-…' ports verbatim
  name: z.string(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  foundedYear: z.number().int().nullable(),
  isbnPrefix: z.string().nullable(),
  doiPrefix: z.string().nullable(),
  isni: z.string().nullable(),
  grpId: z.string().nullable(),
  bowkerId: z.string().nullable(),
  logoKey: z.string().nullable(),
  /** Live COUNT(lib_book WHERE publisher = name) — NAME equality (renames drift; preserved source behavior). */
  booksCount: z.number().int().min(0),
  createdAt: EpochMs,
  ...publisherExtraRead,
});
export type Publisher = z.infer<typeof Publisher>;

const publisherCore = {
  name: z.string().trim().min(1),
  country: z.string(),
  city: z.string(),
  foundedYear: z.number().int(),
  isbnPrefix: z.string(),
  doiPrefix: z.string(),
  isni: z.string(),
  grpId: z.string(),
  bowkerId: z.string(),
  logoKey: z.string(),
};

export const CreatePublisher = z.object({
  name: publisherCore.name,
  country: publisherCore.country.optional(),
  city: publisherCore.city.optional(),
  foundedYear: publisherCore.foundedYear.optional(),
  isbnPrefix: publisherCore.isbnPrefix.optional(),
  doiPrefix: publisherCore.doiPrefix.optional(),
  isni: publisherCore.isni.optional(),
  grpId: publisherCore.grpId.optional(),
  bowkerId: publisherCore.bowkerId.optional(),
  logoKey: publisherCore.logoKey.optional(),
  ...PublisherExtra.shape, // flat → extra_json
});
export type CreatePublisher = z.infer<typeof CreatePublisher>;

export const UpdatePublisher = z.object(publisherCore).partial().extend(PublisherExtra.shape);
export type UpdatePublisher = z.infer<typeof UpdatePublisher>;

/** Keyset list on (name, id); search: name prefix-LIKE. */
export const PublisherListQuery = CursorQuery.extend({ search: z.string().trim().min(1).max(200).optional() });
export type PublisherListQuery = z.infer<typeof PublisherListQuery>;

/**
 * The two UNPAGED registries — subjects and libraries — searched for a
 * typeahead rather than paged. `search` absent means the whole registry, which
 * is what every existing caller asks for and why this stays an array response
 * instead of becoming a keyset page: the picker wants a capped, filtered slice,
 * the registry screens want the lot, and one shape serves both.
 */
export const RegistrySearchQuery = z.object({
  search: z.string().trim().min(1).max(200).optional(),
  /** Cap for a typeahead; absent = unpaged, the registry's historical shape. */
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type RegistrySearchQuery = z.infer<typeof RegistrySearchQuery>;

/* ────────────────────────────── subjects & tags ────────────────────────────── */

export const Subject = z.object({
  id: EntityId, // 'su-…' ports verbatim
  name: z.string(),
  code: z.string().nullable(), // Dewey class, e.g. '005'
  /** A categorization hue NAME (D2, 0022) — never a hex; the screen paints it two-tone, print the static 900. */
  color: CategoryHue,
  description: z.string().nullable(),
  /** Live COUNT(lib_book WHERE subject = name) — stored counter dropped. */
  booksCount: z.number().int().min(0),
  createdAt: EpochMs,
});
export type Subject = z.infer<typeof Subject>;

const subjectCore = {
  name: z.string().trim().min(1),
  code: z.string(),
  color: CategoryHue,
  description: z.string(),
};

export const CreateSubject = z.object({
  name: subjectCore.name,
  code: subjectCore.code.optional(),
  // No Zod default: an omitted colour takes hueForName(name) in the service (the
  // same hash every chip uses for an unnamed category), so two new subjects are
  // not both indigo. The DDL's '#2563eb' default is unreachable — every insert
  // supplies the column.
  color: subjectCore.color.optional(),
  description: subjectCore.description.optional(),
});
export type CreateSubject = z.infer<typeof CreateSubject>;

export const UpdateSubject = z.object(subjectCore).partial();
export type UpdateSubject = z.infer<typeof UpdateSubject>;

/** Standalone registry — links to nothing (pinned); list/create/delete only. */
export const Tag = z.object({
  id: EntityId, // 'tg-…' ports verbatim
  name: z.string(), // UNIQUE — dup surfaces as LIB_TAG_NAME_TAKEN
  /** A categorization hue NAME (D2, 0022); the chip's colour on every title carrying the tag. */
  color: CategoryHue,
  description: z.string().nullable(),
  createdAt: EpochMs,
});
export type Tag = z.infer<typeof Tag>;

export const CreateTag = z.object({
  name: z.string().trim().min(1),
  /** Omitted → hueForName(name) in the service (the subject rule). */
  color: CategoryHue.optional(),
  description: z.string().optional(),
});
export type CreateTag = z.infer<typeof CreateTag>;

/** PATCH /tags/:id — truly-partial rename/recolor/describe (the source's updateTag). */
export const UpdateTag = z
  .object({
    name: z.string().trim().min(1),
    color: CategoryHue,
    description: z.string(),
  })
  .partial();
export type UpdateTag = z.infer<typeof UpdateTag>;
